<template>
  <form class="add-row">
    <div v-if="!isBase" class="add-status">
      <div class="input-group">
        <input
          id="name"
          ref="input"
          v-model="title"
          v-validate="'required'"
          type="text"
          :class="{ error: errors.has('name'), valid: fields.name ? fields.name.valid : null }"
          :style="{ borderColor: errors.has('name') ? '#ed6f6e' : '' }"
          name="name"
          :placeholder="$tr('settings.statuses.newStatusName')"
          :disabled="attachToStatus || isEditNameDisabled"
          @input="editName"
          @keyup.enter.stop="save"
        />
        <div
          v-if="title"
          v-tooltip="{
            content: isTitleDuplicate
              ? $tr('vacancy.emailTemplates.nameDisabled')
              : $tr('vacancy.emailTemplates.nameFree'),
            placement: 'top-center',
            classes: 'tight'
          }"
          class="input-icon"
        >
          <icon-warning v-if="isTitleDuplicate" color="#EA1D27" />
          <icon-success v-else stroke="#3EAF29" />
        </div>
      </div>
    </div>
    <label v-if="errors.has('name')" class="helper">{{ $tr('common.requiredField') }}</label>

    <div v-if="!isBase && !isEditNameDisabled" class="row-block">
      <div class="row-content">
        <div class="checkbox-wrapper">
          <div>
            <vchecklabel v-model="attachToStatus" @input="attachToStatus" />
          </div>
          <label class="input-text" for="status-checkbox"
            >{{ $tr('vacancy.emailTemplates.attachToStatus') }}:</label
          >
        </div>
        <div>
          <multiselect
            v-model="selectedStatus"
            :options="restStatuses"
            class="big direction-left"
            :class="{ error: is_status_name_disabled }"
            open-direction="bottom"
            label="name"
            track-by="id"
            :placeholder="$tr('vacancy.emailTemplates.selectStatus')"
            :show-labels="false"
            :allow-empty="true"
            :searchable="true"
            @select="selectStatus"
          />
        </div>
      </div>
    </div>

    <div class="row-block row--subject">
      <label class="editor-label">{{ $tr('vacancy.emailTemplates.letterSubject') }}</label>
      <div class="input-group">
        <input
          id="subject"
          v-model="subject"
          v-validate="'required'"
          type="text"
          :class="{
            error: errors.has('subject'),
            valid: fields.subject ? fields.subject.valid : null
          }"
          :style="{ borderColor: errors.has('subject') ? '#ed6f6e' : '' }"
          name="subject"
          :placeholder="$tr('vacancy.emailTemplates.textSubject')"
        />
      </div>
      <label v-if="errors.has('subject')" class="helper">{{ $tr('common.requiredField') }}</label>
    </div>

    <div>
      <label class="editor-label">{{ $tr('vacancy.emailTemplates.letterText') }}</label>
      <vcv-editor
        id="toolbar"
        ref="quill"
        v-model="description"
        v-validate="'required'"
        :toolbar-buttons="{ bold: true, italic: true, list: true, link: true, vars: true }"
        :vars="editor_vars"
        class="mb-20"
        :class="{ valid: fields.toolbar ? fields.toolbar.valid : null }"
        :placeholder="$tr('vacancy.emailTemplates.textPlaceholder')"
        name="toolbar"
      />
      <label v-if="errors.has('toolbar')" class="helper">{{ $tr('common.requiredField') }}</label>
    </div>

    <div v-if="(selectedStatus && attachToStatus) || isBase" class="row-block row-flex">
      <div class="radio-wrapper">
        <input
          id="radio-auto"
          v-model="sendingMethod"
          class="vcv-radio"
          :disabled="is_status_new"
          type="radio"
          value="0"
        />
        <label for="radio-auto"></label>
        <span class="input-text">{{ $tr('vacancy.emailTemplates.sendAuto') }}</span>
      </div>
      <div v-if="!is_status_new" class="radio-wrapper">
        <input
          id="radio-manual"
          v-model="sendingMethod"
          class="vcv-radio"
          :disabled="is_status_new"
          type="radio"
          value="1"
        />
        <label for="radio-manual"></label>
        <span class="input-text">{{ $tr('vacancy.emailTemplates.sendManual') }}</span>
      </div>
    </div>

    <label v-if="errorMessage" class="helper">{{ errorMessage }}</label>

    <div class="section-footer">
      <div class="group">
        <vbutton
          class="accept"
          label="vacancy.emailTemplates.save"
          :disabled="!submitButtonEnabled || is_status_name_disabled"
          @click="save"
        />
        <vbutton class="cancel" color="grey" label="settings.statuses.cancel" @click="cancel" />
      </div>
    </div>
  </form>
</template>

<script>
  import { VueEditor } from 'vue2-editor';
  import Trigger from '@/components/shared/MinimalisticTrigger.vue';

  import IconSuccess from '@/components_new/svgs/icon-success.vue';
  import IconWarning from '@/components_new/svgs/icon-warning.vue';

  const MAX_LENGTH_NAME = 36;

  export default {
    name: 'AddOrEditStatus',

    components: {
      VueEditor,
      Trigger,
      IconSuccess,
      IconWarning
    },

    props: {
      model: {
        type: Object,
        default: {}
      },

      templates: Array,

      initialName: {
        type: String,
        default: ''
      },

      callbackWithName: {
        type: Function,
        required: true
      },

      isNew: {
        type: Boolean,
        default: false
      },

      templatesNames: {
        type: Array,
        default: []
      },

      language: {
        type: Object,
        default: {}
      },

      errorMessage: {
        type: String,
        default: ''
      }
    },

    data() {
      return {
        title: this.initialName,
        sendingMethod: 0,
        MAX_LENGTH_NAME: MAX_LENGTH_NAME,
        selectedStatus: null,
        subject: '',
        description: '',
        is_status_new: false,
        attachToStatus: false,

        editor_vars: [
          { id: 'company_name', name: this.$tr('common.variables.company_name') },
          { id: 'vacancy_title', name: this.$tr('common.variables.vacancy_title') },
          { id: 'candidate_name', name: this.$tr('common.variables.candidate_name') },
          { id: 'full_name', name: this.$tr('common.variables.full_name') },
          { id: 'recruiter_firstname', name: this.$tr('common.variables.recruiter_firstname') },
          { id: 'recruiter_lastname', name: this.$tr('common.variables.recruiter_lastname') },
          { id: 'recruiter_phone', name: this.$tr('common.variables.recruiter_phone') },
          { id: 'recruiter_email', name: this.$tr('common.variables.recruiter_email') },
          { id: 'recruiter_position', name: this.$tr('common.variables.recruiter_position') },
          { id: 'interview_link', name: this.$tr('common.variables.interview_link') }
        ]
      };
    },

    computed: {
      isBase() {
        if (!this.model) {
          return false;
        }

        return this.model.is_standard;
      },

      isEditNameDisabled() {
        if (!this.isNew) {
          if (this.model.response_status) {
            return true;
          } else {
            return false;
          }
        } else {
          return false;
        }
      },

      submitButtonEnabled() {
        const fields = Object.keys(this.fields).map(k =>
          Object.assign({}, this.fields[k], { key: k })
        );
        return fields
          .filter(f => f.required)
          .every(f => {
            if (f.key === 'name') {
              return f.valid || this.title;
            } else {
              return f.valid === true;
            }
          });
      },

      is_status_name_disabled() {
        if (!this.attachToStatus) {
          return false;
        }

        if (!this.selectedStatus) {
          return true;
        }
      },

      isTitleDuplicate() {
        if (!this.title) {
          return null;
        }

        const title = this.templatesNames.find(template => template === this.title.trim());

        return title;
      },

      currentLocale() {
        return this.$store.state.locale.value;
      },

      statuses() {
        return this.$store.getters['dictionary/responseStatuses'];
      },

      templatesIds() {
        if (!this.templates) {
          return [];
        }

        return this.templates
          .filter(template => template.response_status && template.response_status.id)
          .map(template => {
            return template.response_status.id;
          });
      },

      restStatuses() {
        if (!this.statuses || !this.templatesIds) {
          return [];
        }

        return this.statuses.filter(status => {
          let isExist = false;
          this.templatesIds.forEach(item => {
            if (+status.id === +item) {
              isExist = true;
            }
          });
          return !isExist;
        });
      }
    },

    mounted() {
      this.$refs.input.focus();
    },

    beforeMount() {
      this.fetch();

      if (this.model) {
        this.subject = this.model.template_header;
        this.description = this.model.template_text;
        this.sendingMethod = this.model.manual !== undefined ? +this.model.manual : 1;

        if (
          this.model.is_standard ||
          (this.model.response_status && this.model.response_status.id)
        ) {
          this.selectedStatus = Object.assign({}, this.model.response_status, {
            name: this.model.response_status.title
          });
          this.attachToStatus = true;
        }
        if (
          this.model.response_status &&
          this.model.response_status.id === 1 &&
          this.model.manual === 0
        ) {
          this.is_status_new = true;
          this.sendingMethod = 0;
        }
      }
    },

    methods: {
      editName() {
        if (this.title.length > MAX_LENGTH_NAME) {
          this.title = this.title.slice(0, MAX_LENGTH_NAME);
        }
      },

      toggleAttachToStatus(val) {
        if (!this.attachToStatus) {
          this.selectedStatus = null;
          this.title = '';
        }

        if (this.attachToStatus && this.selectedStatus) {
          if (this.title.indexOf(this.selectedStatus.name) === -1) {
            this.title = this.selectedStatus.name;
          }
        }
      },

      selectStatus(val) {
        if (val) {
          this.attachToStatus = true;
          if (this.title.indexOf(val.name) === -1) {
            this.title = val.name;
          }
          this.title = val.name;
        }

        if (val && +val.id === 1) {
          this.sendingMethod = 0;
          this.is_status_new = true;
        } else {
          this.is_status_new = false;
        }
      },

      fetch() {
        this.$store.dispatch('dictionary/fetch', true);
      },

      save() {
        this.$emit('save', {
          title: this.title,
          template_text: this.description,
          template_header: this.subject,
          id: this.model.id,
          response_status: {
            id: this.selectedStatus ? this.selectedStatus.id : null
          },
          language: this.language,
          manual: this.sendingMethod !== undefined ? +this.sendingMethod : 1
        });
      },

      cancel() {
        this.$emit('close');
      }
    }
  };
</script>

<style lang="scss" scoped>
@import '@/styles/colors.scss';
@import '@/styles/variables.scss';

.mb-20 {
  margin-bottom: 20px;
}
.add-row {
  width: 100%;
  padding-top: 6px;
}

.add-status {
  width: 100%;
  margin-bottom: 5px;
  padding: 10px 20px 10px 10px;
  background: #f1f2f2;
  border-bottom: 1px solid #dddddd;
  box-sizing: border-box;
  display: flex;

  .input-group {
    display: flex;
    position: relative;
    width: 100%;
  }

  .input-icon {
    position: absolute;
    right: 10px;
    top: 0;
    max-width: 18px;
    bottom: 0;
    display: flex;
    align-items: center;
    color: red;

    img {
      max-width: 100%;
      color: red;
    }
  }

  input {
    font-size: 16px;
    padding: 10px 55px 10px 15px;
    height: 40px;
    border: 1px solid #dddddd;
    border-radius: 5px;
    color: $text-color;
    width: 100%;
  }

  .counter {
    position: absolute;
    right: 45px;
    line-height: 40px;
    color: #a8a8a8;
  }

  button {
    border: none;

    &:hover {
      cursor: pointer;
    }
  }

  .accept {
    height: 40px;
    width: 40px;
    min-width: 40px;
    background-color: $green;
    background-image: url('/static/images/elements/trigger_on.svg');
    background-position: center;
    background-repeat: no-repeat;
    background-size: 15px;
    border-radius: 0 5px 5px 0;
  }

  .cancel {
    margin-left: 20px;
    color: $green;
    font-weight: 500;
    font-size: 16px;
    background: transparent;
  }
}

.editor-label {
  display: block;
  margin-bottom: 3px;
  margin-left: 12px;
  font-size: 14px;
  color: #7f7f7f;
}

.email-editor {
  margin-bottom: 20px;

  .quillWrapper {
    .ql-container {
      &.ql-snow {
        border-top: 0;
      }
    }
  }
}

.group {
  button {
    margin-right: 18px;

    &:last-child {
      margin-right: 0;
    }
  }
}

.row-content {
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  padding: 10px 0;
}

.checkbox-wrapper,
.radio-wrapper {
  display: flex;
  align-items: center;
}

.radio-wrapper {
  margin-right: 20px;

  &:last-child {
    margin-right: 0;
  }

  label {
    z-index: 0;
  }
}

.row-flex {
  display: flex;
  align-items: center;
  margin-bottom: 20px;
}

.row--subject {
  flex-direction: column;
  width: 100%;
  align-items: flex-start;
  margin-bottom: 15px;

  .input-group,
  input {
    width: 100%;
  }
}

.input-text {
  margin-left: 12px;
  display: inline-block;
  vertical-align: middle;
}

label.helper {
  display: block;
  margin-top: 4px;
  margin-bottom: 14px;
  color: #ed6f6e;
}
</style>
